import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Stack, Typography } from '@mui/material';

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

const THUMBNAIL_COUNT = 12;
const FILMSTRIP_HEIGHT = 56;
const HANDLE_WIDTH = 12;
const THUMB_WIDTH = 80;

interface VideoTimelineProps {
  videoUrl: string;
  videoDuration: number;
  clipDuration: number;
  startTime: number;
  onStartTimeChange: (startTime: number) => void;
}

export default function VideoTimeline({
  videoUrl,
  videoDuration,
  clipDuration,
  startTime,
  onStartTimeChange,
}: VideoTimelineProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const dragOffsetX = useRef(0);
  const [localStart, setLocalStart] = useState(startTime);
  const [thumbnails, setThumbnails] = useState<Array<string | null>>(Array(THUMBNAIL_COUNT).fill(null));
  const [loadingThumbnails, setLoadingThumbnails] = useState(true);
  const blobUrlsRef = useRef<Array<string>>([]);

  useEffect(() => {
    setLocalStart(startTime);
  }, [startTime]);

  useEffect(() => {
    if (!videoUrl || videoDuration <= 0) {
      return;
    }

    let cancelled = false;
    setLoadingThumbnails(true);
    setThumbnails(Array(THUMBNAIL_COUNT).fill(null));

    blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    blobUrlsRef.current = [];

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.preload = 'auto';
    video.src = videoUrl;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = THUMB_WIDTH;
    canvas.height = FILMSTRIP_HEIGHT;

    let currentIndex = 0;

    const extractFrame = () => {
      if (cancelled || !ctx) {
        return;
      }
      const time = (currentIndex / THUMBNAIL_COUNT) * videoDuration;
      video.currentTime = Math.min(time, videoDuration - 0.1);
    };

    video.onseeked = () => {
      if (cancelled || !ctx) {
        return;
      }
      ctx.drawImage(video, 0, 0, THUMB_WIDTH, FILMSTRIP_HEIGHT);

      canvas.toBlob(
        (blob) => {
          if (cancelled || !blob) {
            return;
          }
          const url = URL.createObjectURL(blob);
          blobUrlsRef.current.push(url);

          const idx = currentIndex;
          setThumbnails((prev) => {
            const next = [...prev];
            next[idx] = url;
            return next;
          });

          currentIndex++;
          if (currentIndex < THUMBNAIL_COUNT) {
            extractFrame();
          } else {
            setLoadingThumbnails(false);
            video.src = '';
            video.remove();
          }
        },
        'image/jpeg',
        0.5,
      );
    };

    video.addEventListener('loadeddata', () => {
      if (!cancelled) {
        extractFrame();
      }
    });

    video.addEventListener('error', () => {
      if (!cancelled) {
        setLoadingThumbnails(false);
      }
    });

    return () => {
      cancelled = true;
      video.src = '';
      video.remove();
    };
  }, [videoUrl, videoDuration]);

  useEffect(() => {
    const urls = blobUrlsRef.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const maxStart = Math.max(0, videoDuration - clipDuration);
  const windowFraction = videoDuration > 0 ? clipDuration / videoDuration : 1;

  const getStartFromClientX = useCallback(
    (clientX: number) => {
      if (!trackRef.current) {
        return localStart;
      }
      const rect = trackRef.current.getBoundingClientRect();
      const innerWidth = rect.width - HANDLE_WIDTH * 2;
      const relX = clientX - rect.left - HANDLE_WIDTH - dragOffsetX.current;
      const fraction = relX / innerWidth;
      const newStart = fraction * videoDuration;
      return Math.max(0, Math.min(maxStart, newStart));
    },
    [videoDuration, maxStart, localStart],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!trackRef.current) {
        return;
      }
      isDragging.current = true;
      const rect = trackRef.current.getBoundingClientRect();
      const innerWidth = rect.width - HANDLE_WIDTH * 2;
      const windowLeft = (localStart / videoDuration) * innerWidth;
      const windowWidth = windowFraction * innerWidth;
      const clickX = e.clientX - rect.left - HANDLE_WIDTH;

      if (clickX >= windowLeft && clickX <= windowLeft + windowWidth) {
        dragOffsetX.current = clickX - windowLeft;
      } else {
        dragOffsetX.current = windowWidth / 2;
        const newStart = getStartFromClientX(e.clientX);
        setLocalStart(newStart);
      }
      e.preventDefault();
    },
    [localStart, videoDuration, windowFraction, getStartFromClientX],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging.current) {
        return;
      }
      const newStart = getStartFromClientX(e.clientX);
      setLocalStart(newStart);
    },
    [getStartFromClientX],
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging.current) {
      isDragging.current = false;
      onStartTimeChange(localStart);
    }
  }, [localStart, onStartTimeChange]);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const endTime = Math.min(localStart + clipDuration, videoDuration);
  const windowLeftPercent = videoDuration > 0 ? (localStart / videoDuration) * 100 : 0;
  const windowWidthPercent = windowFraction * 100;

  const hasAnyThumbnail = thumbnails.some((t) => t !== null);

  return (
    <Box sx={{ width: '100%', userSelect: 'none' }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Start: {formatTime(localStart)}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          Clip: {formatTime(clipDuration)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          End: {formatTime(endTime)}
        </Typography>
      </Stack>

      <Box
        ref={trackRef}
        onMouseDown={handleMouseDown}
        sx={{
          position: 'relative',
          height: FILMSTRIP_HEIGHT,
          borderRadius: 1,
          overflow: 'hidden',
          cursor: 'pointer',
          bgcolor: 'grey.900',
        }}
      >
        {hasAnyThumbnail && (
          <Stack direction="row" sx={{ position: 'absolute', inset: 0 }}>
            {thumbnails.map((thumb, i) => (
              <Box
                key={i}
                sx={{
                  height: '100%',
                  flex: '1 0 0',
                  bgcolor: 'grey.800',
                  overflow: 'hidden',
                }}
              >
                {thumb && (
                  <Box
                    component="img"
                    src={thumb}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                )}
              </Box>
            ))}
          </Stack>
        )}

        {loadingThumbnails && !hasAnyThumbnail && (
          <Stack alignItems="center" justifyContent="center" sx={{ position: 'absolute', inset: 0 }}>
            <Typography variant="caption" color="grey.400">
              Loading frames...
            </Typography>
          </Stack>
        )}

        {/* Dimmed overlay outside the selection */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: `${windowLeftPercent}%`,
            bgcolor: 'rgba(0,0,0,0.6)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 0,
            left: `${windowLeftPercent + windowWidthPercent}%`,
            bgcolor: 'rgba(0,0,0,0.6)',
            pointerEvents: 'none',
          }}
        />

        {/* Selection window with handles */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${windowLeftPercent}%`,
            width: `${windowWidthPercent}%`,
            pointerEvents: 'none',
            borderTop: '3px solid',
            borderBottom: '3px solid',
            borderColor: 'warning.main',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: -1,
              top: 0,
              bottom: 0,
              width: HANDLE_WIDTH,
              bgcolor: 'warning.main',
              borderRadius: '4px 0 0 4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ width: 2, height: 16, bgcolor: 'warning.dark', borderRadius: 1 }} />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              right: -1,
              top: 0,
              bottom: 0,
              width: HANDLE_WIDTH,
              bgcolor: 'warning.main',
              borderRadius: '0 4px 4px 0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ width: 2, height: 16, bgcolor: 'warning.dark', borderRadius: 1 }} />
          </Box>
        </Box>
      </Box>

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {formatTime(0)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatTime(videoDuration)}
        </Typography>
      </Stack>
    </Box>
  );
}
